import { useState } from "react"
import { useGeneralContext } from "../../../Contexts/GeneralContext/GeneralProvider"
import { useSchoolAdminModalContext } from "../../../Contexts/SchoolAdminContext/SchoolAdminProvider"
import ListAllUsers from "../../../Components/SchoolAdminModalsComponents/ListData/ListAllUsers"
import ListAllStudents from "../../../Components/SchoolAdminModalsComponents/ListData/ListAllStudents"
import UploadPopUp from "../../../Components/SchoolAdminModalsComponents/UploadData/UploadPopUp"
import { PrepareFileToSend } from "../../../Helpers/PrepareHelpers"
import { FetchPostFunction, FetchPutFunction } from "../../../Helpers/FetchHelpers"
import { GroupInterface, RoleInterface } from "../../../Interfaces/Entities"
import "../SchoolAdminCSS.css"


function SchoolAdminSAUModal() {

	const [currentlySeen, setCurrentlySeen] = useState<'Users' | 'Students'>('Users')
	const [isShowingPopUp, setIsShowingPopUp] = useState<boolean>(false)
	const { setCurrentModal, schoolOfUser } = useGeneralContext()
	const { schoolGroups } = useSchoolAdminModalContext()

	const handleCreateElement = () => {
		if (currentlySeen === 'Users') {
			setCurrentModal('CreateUser')
		} else {
			setCurrentModal('CreateStudent')
		}
	}

	const parseCSV = async (file: File): Promise<Array<{ [key: string]: string }>> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader()
			reader.onload = (e) => {
				try {
					const text = e.target?.result as string
					const lines = text.split('\n').filter(line => line.trim() !== '')
					if (lines.length < 2) {
						resolve([])
						return
					}
					
					// Parse header
					const headers = lines[0].split(',').map(h => h.trim())
					
					// Parse data rows
					const data: Array<{ [key: string]: string }> = []
					for (let i = 1; i < lines.length; i++) {
						const values: string[] = []
						let currentValue = ''
						let insideQuotes = false
						
						for (let j = 0; j < lines[i].length; j++) {
							const char = lines[i][j]
							if (char === '"') {
								insideQuotes = !insideQuotes
							} else if (char === ',' && !insideQuotes) {
								values.push(currentValue.trim())
								currentValue = ''
							} else {
								currentValue += char
							}
						}
						values.push(currentValue.trim())
						
						const row: { [key: string]: string } = {}
						headers.forEach((header, index) => {
							row[header] = values[index] || ''
						})
						data.push(row)
					}
					
					resolve(data)
				} catch (error) {
					reject(error)
				}
			}
			reader.onerror = () => reject(new Error('Error reading file'))
			reader.readAsText(file)
		})
	}

	const findGroupByName = (groupName: string): GroupInterface | undefined => {
		return schoolGroups.find((group: GroupInterface) => 
			group.name?.toLowerCase().trim() === groupName.toLowerCase().trim() ||
			group.SK?.toLowerCase().trim() === groupName.toLowerCase().trim()
		)
	}

	const getTemplatePriorities = (templateName: string): RoleInterface => {
		if (!schoolOfUser || !schoolOfUser.templates) {
			return { "General": "mid" }
		}

		// Look for template by exact name match (case-insensitive)
		const templateKey = Object.keys(schoolOfUser.templates).find(
			key => key.toLowerCase().trim() === templateName.toLowerCase().trim()
		)

		if (templateKey && schoolOfUser.templates[templateKey]) {
			return schoolOfUser.templates[templateKey]
		}

		// If template not found, use default
		return { "General": "mid" }
	}

	const assignGroupsToUsers = async (csvData: Array<{ [key: string]: string }>) => {
		if (!schoolOfUser) {
			return
		}

		for (const row of csvData) {
			const email = row['Mail']?.trim()
			const gruposValue = row['Grupos']?.trim()
			const prioridadesDefault = row['Prioridades default']?.trim()
			
			if (!email || !gruposValue) {
				continue
			}

			// Get template priorities from "Prioridades default" column
			const templatePriorities = prioridadesDefault 
				? getTemplatePriorities(prioridadesDefault)
				: { "General": "mid" }

			// Split groups by comma and trim each group name
			const groupNames = gruposValue.split(',').map(name => name.trim()).filter(name => name !== '')
			
			if (groupNames.length === 0) {
				continue
			}

			// Find groups by name and assign template priorities
			const groupsToAdd: { [groupSK: string]: RoleInterface } = {}
			for (const groupName of groupNames) {
				const group = findGroupByName(groupName)
				if (group) {
					// Use template priorities for this group
					groupsToAdd[group.SK] = templatePriorities
				}
			}

			// Update user groups if we found any groups
			if (Object.keys(groupsToAdd).length > 0) {
				try {
					const url = `${process.env.REACT_APP_BACKEND_URL}/user-groups/${email}?school_id=${schoolOfUser.SK}`
					const body = {
						"groups": groupsToAdd,
						"to_delete": {}
					}
					await FetchPutFunction(url, body)
				} catch (error) {
					console.error(`Error assigning groups to user ${email}:`, error)
				}
			}
		}
	}

	const handleSubmitFile = async (file: File) => {
		if (!schoolOfUser) {
			return
		}
		try {
			// Parse CSV first before file is consumed by PrepareFileToSend
			let csvData: Array<{ [key: string]: string }> = []
			if (currentlySeen === 'Users') {
				try {
					csvData = await parseCSV(file)
				} catch (error) {
					console.error('Error parsing CSV:', error)
					// Continue with upload even if CSV parsing fails
				}
			}

			const correctUrlCode = currentlySeen === 'Users' ? 'users' : 'groups'
			const url = `${process.env.REACT_APP_BACKEND_URL}/upload-${correctUrlCode}?` +
				`school_id=${schoolOfUser.SK}`
			const files = await PrepareFileToSend(file);
			const requestBody = {
				files: files,
			};
			await FetchPostFunction(url, requestBody)
			const correctCompleteMessage = currentlySeen === 'Users' ? 'Usuarios correctamente creados' :
				'Estudiantes correctamente actualizados'
			alert(correctCompleteMessage)
			
			// If uploading users, assign groups from CSV
			if (currentlySeen === 'Users' && csvData.length > 0) {
				try {
					await assignGroupsToUsers(csvData)
				} catch (error) {
					console.error('Error processing groups from CSV:', error)
					// Don't show error to user as users were already created successfully
				}
			}
		} catch (error) {
			alert("Hubo un problema al subir la carga masiva")
		}

	}

	return (
		<>
			<div className="ETButtonDiv">
				<button
					type='button'
					onClick={() => setCurrentlySeen('Users')}
					className={currentlySeen === 'Users' ? 'SelectedButton ETButton' : 'UnSelectedButton ETButton'}
				>
					Usuarios
				</button>
				<button
					type='button'
					onClick={() => setCurrentlySeen('Students')}
					className={currentlySeen === 'Students' ? 'SelectedButton ETButton' : 'UnSelectedButton ETButton'}
				>
					Estudiantes
				</button>
			</div>
			<div className="StudentsAndUserButtonDiv">
				<div className="StudentsAndUserDiv">
					<button type='button' onClick={handleCreateElement}>
						Crear nuevo
					</button>
					<button type='button' onClick={() => setIsShowingPopUp(true)}>
						Cargar CSV
					</button>
					<a href={currentlySeen === 'Users' ? '/Assets/PlantillaUsuarios.csv' : '/Assets/PlantillaGrupos.csv'} 
						download={currentlySeen === 'Users' ? 'PlantillaUsuarios.csv' : 'PlantillaGrupos.csv'}>
						<button type='button'>
							Descargar plantilla
						</button>
					</a>
				</div>
				{isShowingPopUp && (
					<UploadPopUp
						onClose={() => setIsShowingPopUp(false)}
						onSubmit={handleSubmitFile}
					/>
				)}
			</div>
			{currentlySeen === 'Users' ? (
				<ListAllUsers />
			) : (
				<ListAllStudents />
			)}
		</>
	)
}

export default SchoolAdminSAUModal